import React, { memo, useMemo, useRef, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { Row, Col, Divider, Button, Tooltip } from 'antd';
import { Form, Input, Select } from 'formik-antd';
import * as Yup from 'yup';
import { has, isNil, isNumber } from 'lodash';
import bankersRounding from 'bankers-rounding';
import { useDidUpdate } from 'rooks';
import cx from 'classnames';
import moment from 'moment';

import {
  PayTypeEnum,
  OvertimeEnum,
  AssignmentTypeEnum,
  AssignmentMultiRateEditModel,
  CustomerMspOrVmsOverviewDto,
} from 'api/backofficeApiClient';
import FormItem from 'components/formik/FormItem';
import { getDecimalSafeValue } from 'utils/mathUtils';
import InputNumber from 'components/formik/InputNumber';
import FormikEffect from 'components/formik/FormikEffect';
import { FinancialInfoCalculator } from './calculationUtils';
import {
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import {
  isAdminSelector,
  isTeammateSelector,
} from 'redux/selectors/authenticationSelectors';
import {
  getFinancialWarningMessages,
  getOvertimeOptions,
  getPayTypeOptions,
} from 'utils/assignmentUtils';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import FinancialRatesFieldArray from './FinancialRatesFieldArray';
import {
  initialMultipleRatesValidationSchema,
  submitMultipleRatesValidationSchema,
} from './FinancialRatesFieldArray/FinancialRatesSectionArray/FinancialRatesSection';
import FinancialInfoSchedulerModal, {
  EffectiveDateMode,
  FinancialSchedulerFormValues,
} from '../../FinancialInfoSchedulerModal';
import { IS_PRODUCTION_ENV, IS_QA_ENV } from 'constants/env.constants';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { getFormattedNumber } from 'utils/numberUtils';

import styles from './FinancialInfo.module.less';

export interface WorkSiteWorkLocation {
  country?: number;
  state?: number;
  internationalStateName?: string;
  city?: string;
  internationalCityName?: string;
  address?: string;
  zip?: string;
}

export interface SimplePriceAgreementW2Info {
  w2Price?: number;
  placementStates?: (number | undefined)[];
}

type Props = {
  formRef: any;
  isPayTypeVisible?: boolean;
  initialValues?: FinancialInfoFormValues;
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto;
  workSiteWorkLocation: WorkSiteWorkLocation;
  selectedAssignmentType: AssignmentTypeEnum | null;
  onChange: (formik: FormikProps<FinancialInfoFormValues>) => void;
  w2InfoByWorkSiteWorkLocation?: SimplePriceAgreementW2Info;
  initialMBPRate?: number;
  predefinedFinancialSchedulerValues?: FinancialSchedulerFormValues;
  onSetPredefinedFinancialScheduler: (
    data: FinancialSchedulerFormValues,
  ) => void;
  onResetFinancialInfoSchedulers: () => void;
};

export interface FinancialInfoFormValues {
  mbpRate?: number | null;
  billRate?: number | null;
  payRate?: number | null;
  profitRate?: number | null;
  overtimePayRate?: number | null;
  overtimeBillRate?: number | null;
  overtime?: OvertimeEnum | null;
  payType?: PayTypeEnum | null;
  dtBillRate?: number | null;
  dtPayRate?: number | null;
  multiRates?: AssignmentMultiRateEditModel[];
}

const getInitialValidationSchema = (
  isViewDTBillRateDtPayRateFields: boolean,
  isTeammate?: boolean,
) => {
  const schema: any = {
    billRate: Yup.number()
      .nullable()
      .min(0, 'Bill rate should be from 0 to 999999')
      .max(999999, 'Bill rate should be from 0 to 999999'),
    payRate: Yup.number()
      .nullable()
      .min(0, 'Pay rate should be from 0 to 999999')
      .max(999999, 'Pay rate should be from 0 to 999999')
      .when('billRate', {
        is: (billRate: number | null) => isNumber(billRate),
        then: Yup.number()
          .nullable()
          .max(Yup.ref('billRate'), 'Pay rate should be less than bill rate'),
      }),
    overtimeBillRate: Yup.number()
      .nullable()
      .min(0, 'OT bill rate should be from 0 to 999999')
      .max(999999, 'OT bill rate should be from 0 to 999999'),
    overtimePayRate: Yup.number()
      .nullable()
      .min(0, 'OT pay rate should be from 0 to 999999')
      .when('overtime', {
        is: (overtime: OvertimeEnum | null) =>
          overtime === OvertimeEnum.Nonexempt,
        then: Yup.number()
          .nullable()
          .max(999999, 'OT pay rate should be from 0 to 999999'),
      }),
    profitRate: Yup.number().nullable(),
    overtime: Yup.number().nullable(),
    multiRates: Yup.array().of(
      Yup.object(initialMultipleRatesValidationSchema),
    ),
  };

  if (isViewDTBillRateDtPayRateFields) {
    schema.dtBillRate = Yup.number()
      .nullable()
      .min(0, 'DT Bill rate should be from 0 to 999999')
      .max(999999, 'DT Bill rate should be from 0 to 999999');
    schema.dtPayRate = Yup.number().nullable();
  }

  if (!isTeammate) {
    schema.profitRate = Yup.number()
      .nullable()
      .min(0, 'Profit rate should be from 0 to 999999')
      .max(999999, 'Profit rate should be from 0 to 999999');
  }

  return schema;
};

const getSubmitValidationSchema = (
  isW2Assignment: boolean,
  isViewDTBillRateDtPayRateFields: boolean,
  predefinedFinancialSchedulerValues?: FinancialSchedulerFormValues,
  isTeammate?: boolean,
) => {
  const initialValues = getInitialValidationSchema(
    isViewDTBillRateDtPayRateFields,
    isTeammate,
  );
  const schema: any = {
    ...initialValues,
    billRate: initialValues.billRate.required('Field is required'),
    payRate: initialValues.payRate.required('Field is required'),
    overtimeBillRate: Yup.lazy(() => {
      let schema: any = initialValues.overtimeBillRate;

      if (
        !predefinedFinancialSchedulerValues?.financialInfoScheduler?.hasOwnProperty(
          'overtimeBillRate',
        )
      ) {
        schema = initialValues.overtimeBillRate.when('overtime', {
          is: (overtime: OvertimeEnum | null) =>
            overtime !== OvertimeEnum.Exempt,
          then: initialValues.overtimeBillRate.required('Field is required'),
        });
      }

      return schema;
    }),
    overtimePayRate: initialValues.overtimePayRate.when('overtime', {
      is: (overtime: OvertimeEnum | null) => overtime !== OvertimeEnum.Exempt,
      then: initialValues.overtimePayRate.required('Field is required'),
    }),
    overtime: initialValues.overtime.required('Field is required'),
    multiRates: Yup.array().of(
      Yup.object(
        submitMultipleRatesValidationSchema(predefinedFinancialSchedulerValues),
      ),
    ),
  };

  if (isViewDTBillRateDtPayRateFields) {
    schema.dtBillRate = initialValues.dtBillRate.required('Field is required');
  }
  if (isW2Assignment) {
    schema.payType = Yup.number().nullable().required('Field is required');
  }

  return Yup.object(schema);
};

export default memo((props: Props) => {
  const {
    formRef,
    onChange,
    mspOrVmsOverviewInfo,
    workSiteWorkLocation,
    selectedAssignmentType,
    initialValues: predefinedInitialValues,
    w2InfoByWorkSiteWorkLocation,
    initialMBPRate,
    predefinedFinancialSchedulerValues,
    onSetPredefinedFinancialScheduler,
    onResetFinancialInfoSchedulers,
  } = props;
  const [
    isFinancialInfoSchedulerModalVisible,
    setIsFinancialInfoSchedulerModalVisible,
  ] = useState(false);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const isW2Assignment =
    selectedAssignmentType === AssignmentTypeEnum.W2Assignment;
  const isTeammate = useAppSelector(isTeammateSelector);
  // State Ids are different on prod and dev.
  const californiaStateId = IS_PRODUCTION_ENV || IS_QA_ENV ? 5 : 210;
  const [overtimeValue, setOvertimeValue] = useState(
    predefinedInitialValues?.overtime,
  );
  const isViewDTBillRateDtPayRateFields =
    overtimeValue === OvertimeEnum.Nonexempt &&
    workSiteWorkLocation.state === californiaStateId;
  const otPayRateFieldRef: any = useRef();

  const mapFinancialInfoToInitialValues = (): FinancialInfoFormValues => {
    if (predefinedInitialValues) return predefinedInitialValues;

    return initialValues;
  };

  const initialValues: FinancialInfoFormValues = {
    billRate: null,
    payRate: null,
    overtimeBillRate: null,
    overtimePayRate: null,
    overtime: null,
    payType: null,
    profitRate: null,
    mbpRate: initialMBPRate,
    multiRates: [],
    dtBillRate: null,
  };

  const handleOpenFinancialInfoSchedulerModal = () => {
    setIsFinancialInfoSchedulerModalVisible(true);
  };

  const handleCloseFinancialInfoSchedulerModal = () => {
    setIsFinancialInfoSchedulerModalVisible(false);
  };

  const financialWarningMessages = getFinancialWarningMessages({
    mspVmsValues: mspOrVmsOverviewInfo,
    hasW2InfoByWorkSiteWorkLocation:
      !!w2InfoByWorkSiteWorkLocation ||
      isNil(workSiteWorkLocation.state) ||
      !isW2Assignment,
  });

  const financialWarningMessageExists = !!financialWarningMessages.length;

  const getSafeComputedValue = (value: number) => {
    return financialWarningMessageExists ? 0 : getDecimalSafeValue(value);
  };

  const submitValidationSchema = useMemo(
    () =>
      getSubmitValidationSchema(
        isW2Assignment,
        isViewDTBillRateDtPayRateFields,
        predefinedFinancialSchedulerValues,
        isTeammate,
      ),
    [
      isW2Assignment,
      isTeammate,
      isViewDTBillRateDtPayRateFields,
      predefinedFinancialSchedulerValues,
    ],
  );

  useDidUpdate(() => {
    calculateProfitRateValue(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workSiteWorkLocation?.state, mspOrVmsOverviewInfo]);

  useDidUpdate(() => {
    calculateDTPayRate(false);
    calculateOvertimePayRate(false);
    // eslint-disable-next-line
  }, [isViewDTBillRateDtPayRateFields]);

  const calculateDTPayRate = (dtRateTouched: boolean = true) => {
    if (!isViewDTBillRateDtPayRateFields) return;

    setTimeout(() => {
      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;
      const { payRate } = values;

      const value = bankersRounding((payRate || 0) * 2, 2);

      setFieldValue('dtPayRate', value);
      setFieldTouched('dtPayRate', dtRateTouched);
    }, 0);
  };

  const handlePayRateChange = () => {
    calculateProfitRateValue();
    calculateDTPayRate();
    calculateOvertimePayRate();
  };

  const calculateOvertimePayRate = (overtimePayRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;
      const { payRate, overtime } = values;

      if (overtime === OvertimeEnum.Exempt) return;

      const value = bankersRounding((payRate || 0) * 1.5, 2);

      setFieldValue('overtimePayRate', value);
      setFieldTouched('overtimePayRate', overtimePayRateTouched);
    }, 0);
  };

  const calculateProfitRateValue = (profitRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;
      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;

      // Reset profitRate if billRate ot payRate is not provided
      const shouldResetValue = isNil(values.billRate) || isNil(values.payRate);
      const profitRateValue = FinancialInfoCalculator.getProfitRateValue(
        values,
        mspOrVmsOverviewInfo,
      );
      const safeValue = getSafeComputedValue(profitRateValue);
      setFieldValue('profitRate', shouldResetValue ? 0 : safeValue);
      setFieldTouched('profitRate', profitRateTouched);
    }, 0);
  };

  const handleOvertimeChange = (value: OvertimeEnum) => {
    calculateOvertimePayRate(false);
    setOvertimeValue(value);

    setTimeout(() => {
      if (value === OvertimeEnum.Exempt) {
        formRef.current.setFieldTouched('dtPayRate', false);
        formRef.current.setFieldTouched('dtBillRate', false);
        formRef.current.setFieldTouched('overtimePayRate', false);
        formRef.current.setFieldTouched('overtimeBillRate', false);

        formRef.current.setFieldValue('dtPayRate', null);
        formRef.current.setFieldValue('dtBillRate', null);
        formRef.current.setFieldValue('overtimePayRate', null);
        formRef.current.setFieldValue('overtimeBillRate', null);
      }
    }, 0);
  };

  const calculateMBPRateValue = (mbpRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;

      // The profitRate depends on the mbpRate, because need to update this field too.
      const shouldResetValue = isNil(values.billRate) || isNil(values.payRate);
      const payRateValue = FinancialInfoCalculator.getProfitRateValue(
        values,
        mspOrVmsOverviewInfo,
      );
      const safeValue = getSafeComputedValue(payRateValue);

      setFieldValue('profitRate', shouldResetValue ? 0 : safeValue);
      setFieldValue('mbpRate', values.mbpRate);
      setFieldTouched('mbpRate', mbpRateTouched);
    }, 0);
  };

  const renderChangesEffectiveDateBtn = () => {
    return (
      predefinedFinancialSchedulerValues && (
        <Col xs={24} md={12}>
          <div className={styles.changeBtn}>
            <Button
              className="with-icon"
              icon={<span className="icon icon-edit" />}
              onClick={handleOpenFinancialInfoSchedulerModal}
            >
              Changes effective as of{' '}
              {moment(predefinedFinancialSchedulerValues.effectiveDate).format(
                'MM/DD/YYYY',
              )}
            </Button>
          </div>
        </Col>
      )
    );
  };

  const onRemoveMultiRateScheduler = (index: number) => {
    const multiRateSchedulers =
      predefinedFinancialSchedulerValues?.multiRateSchedulers;

    if (!multiRateSchedulers) return;

    const filtered = multiRateSchedulers
      .filter((multiRateScheduler) => multiRateScheduler.index !== index)
      .map((multiRateScheduler) => {
        const multiRateIndex = multiRateScheduler.index || 0;
        if (multiRateIndex > index) {
          return {
            ...multiRateScheduler,
            index: multiRateIndex - 1,
          };
        }
        return multiRateScheduler;
      });

    onSetPredefinedFinancialScheduler({ multiRateSchedulers: filtered });
  };

  const onRemoveFinancialScheduler = () => {
    onResetFinancialInfoSchedulers();
    handleCloseFinancialInfoSchedulerModal();
  };

  return (
    <>
      {isFinancialInfoSchedulerModalVisible && (
        <FinancialInfoSchedulerModal
          mode={EffectiveDateMode.EDIT}
          initialValues={predefinedFinancialSchedulerValues}
          onClose={handleCloseFinancialInfoSchedulerModal}
          onSuccess={onSetPredefinedFinancialScheduler}
          onRemoveFinancialScheduler={onRemoveFinancialScheduler}
          mspOrVmsOverviewInfo={mspOrVmsOverviewInfo}
          workStateLocation={workSiteWorkLocation.state}
          isW2Assignment={isW2Assignment}
        />
      )}
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={mapFinancialInfoToInitialValues()}
        validationSchema={submitValidationSchema}
        onSubmit={() => {}}
      >
        {({ values }) => (
          <>
            <FormikEffect onChange={onChange} />
            <Form layout="vertical">
              <div className="modal-scroll-wrapper">
                {!isAdmin && (
                  <div className={styles.hint}>
                    <i className="icon icon-info-filled" />
                    All calculations are based upon billing and pay rates for
                    Regular Pay
                  </div>
                )}

                <FinancialRatesFieldArray
                  renderChangesEffectiveDateBtn={renderChangesEffectiveDateBtn}
                  onRemoveMultiRateScheduler={onRemoveMultiRateScheduler}
                  predefinedMultiRates={
                    predefinedFinancialSchedulerValues?.multiRateSchedulers
                  }
                >
                  <Row gutter={40}>
                    {isW2Assignment && (
                      <Col xs={24} md={12}>
                        <FormItem name="payType" label="Pay type *">
                          <Select name="payType" placeholder="Select pay type">
                            {getPayTypeOptions().map((payType) => (
                              <Select.Option
                                key={`select-payType-${payType.value}`}
                                value={payType.value || ''}
                              >
                                {payType.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </FormItem>
                      </Col>
                    )}

                    <Col xs={24} md={12}>
                      <FormItem label="Pay rate *" name="payRate">
                        <InputNumber
                          name="payRate"
                          placeholder="0.00"
                          disabled={has(
                            predefinedFinancialSchedulerValues?.financialInfoScheduler,
                            'payRate',
                          )}
                          className="full-width"
                          precision={2}
                          inputPrefix={'$'}
                          onValueChange={() => handlePayRateChange()}
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem label="Bill rate *" name="billRate">
                        <InputNumber
                          name="billRate"
                          placeholder="0.00"
                          disabled={has(
                            predefinedFinancialSchedulerValues?.financialInfoScheduler,
                            'billRate',
                          )}
                          className="full-width"
                          precision={2}
                          inputPrefix={'$'}
                          onValueChange={() => calculateProfitRateValue()}
                        />
                      </FormItem>
                    </Col>
                    {!isTeammate && (
                      <Col xs={24} md={12}>
                        <FormItem label="Profit rate" name="profitRate">
                          <InputNumber
                            disabled
                            allowNegative
                            name="profitRate"
                            placeholder="0.00"
                            className="full-width"
                            precision={2}
                          />
                        </FormItem>
                      </Col>
                    )}
                    <Col xs={24} md={12}>
                      <FormItem name="overtime" label="Overtime *">
                        <Select
                          name="overtime"
                          placeholder="Select overtime type"
                          onChange={handleOvertimeChange}
                        >
                          {getOvertimeOptions().map((overtime) => (
                            <Select.Option
                              key={`select-overtime-${overtime.value}`}
                              value={overtime.value || ''}
                            >
                              {overtime.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </FormItem>
                    </Col>

                    {values.overtime !== OvertimeEnum.Exempt && (
                      <>
                        <Col xs={24} md={12} ref={otPayRateFieldRef}>
                          <FormItem
                            label={
                              <>
                                <span>OT pay rate *</span>
                                <Tooltip
                                  overlayClassName={cx(
                                    'light-tooltip',
                                    styles.modalTooltip,
                                  )}
                                  title="By default, the OT rate is 1.5x."
                                  trigger="click"
                                  placement="topLeft"
                                  getPopupContainer={() =>
                                    otPayRateFieldRef.current
                                  }
                                  className={styles.modalTooltipOpener}
                                >
                                  <span className="icon icon-attention-filled" />
                                </Tooltip>
                              </>
                            }
                            name="overtimePayRate"
                          >
                            <InputNumber
                              name="overtimePayRate"
                              placeholder="0.00"
                              className="full-width"
                              precision={2}
                              inputPrefix={'$'}
                            />
                          </FormItem>
                        </Col>

                        <Col xs={24} md={12}>
                          <FormItem
                            label="OT bill rate *"
                            name="overtimeBillRate"
                          >
                            <InputNumber
                              name="overtimeBillRate"
                              placeholder="0.00"
                              disabled={has(
                                predefinedFinancialSchedulerValues?.financialInfoScheduler,
                                'overtimeBillRate',
                              )}
                              className="full-width"
                              precision={2}
                              inputPrefix={'$'}
                            />
                          </FormItem>
                        </Col>
                      </>
                    )}
                    {isViewDTBillRateDtPayRateFields && (
                      <>
                        <Col xs={24} md={12}>
                          <FormItem name="dtPayRate" label="DT Pay rate">
                            <InputNumber
                              disabled
                              name="dtPayRate"
                              placeholder="0.00"
                              className="full-width"
                              precision={2}
                              inputPrefix={'$'}
                            />
                          </FormItem>
                        </Col>

                        <Col xs={24} md={12}>
                          <FormItem name="dtBillRate" label="DT Bill rate *">
                            <InputNumber
                              name="dtBillRate"
                              placeholder="0.00"
                              disabled={has(
                                predefinedFinancialSchedulerValues?.financialInfoScheduler,
                                'dtBillRate',
                              )}
                              className="full-width"
                              precision={2}
                              inputPrefix={'$'}
                            />
                          </FormItem>
                        </Col>
                      </>
                    )}
                    {renderChangesEffectiveDateBtn()}
                  </Row>

                  {financialWarningMessageExists && (
                    <>
                      {financialWarningMessages.map((message, index) => (
                        <div
                          key={`financial-message-${index}`}
                          className={cx('hidden-md-up', styles.hintSm)}
                        >
                          {message}
                        </div>
                      ))}
                    </>
                  )}

                  <Row gutter={40}>
                    <Col xs={24} md={12}>
                      <div className="modal-title">Rates Calculator</div>

                      <Row gutter={20}>
                        <Col xs={24} lg={12}>
                          <FormItem name="vmsMspRate" label="VMS/MSP Rate">
                            <InputNumber
                              disabled
                              value={getFormattedNumber(
                                getSafeComputedValue(
                                  FinancialInfoCalculator.getVmsMspRateValue(
                                    mspOrVmsOverviewInfo,
                                  ),
                                ),
                              )}
                              allowNegative
                              name="vmsMspRate"
                              placeholder="0.00"
                              className="full-width"
                              precision={2}
                            />
                          </FormItem>
                        </Col>

                        <Col xs={24} lg={12}>
                          <FormItem
                            label="VMS/MSP Portion"
                            name="vmsMspPortion"
                          >
                            <InputNumber
                              disabled
                              value={getSafeComputedValue(
                                FinancialInfoCalculator.getVmsMspPortionValue(
                                  values.billRate,
                                  mspOrVmsOverviewInfo,
                                ),
                              )}
                              allowNegative
                              name="vmsMspPortion"
                              inputPrefix="$"
                              placeholder="0.00"
                              className="full-width"
                              precision={2}
                            />
                          </FormItem>
                        </Col>
                        {!isTeammate && (
                          <>
                            <Col xs={24} lg={12}>
                              <FormItem label="MBP Rate" name="mbpRate">
                                <InputNumber
                                  disabled={!isAdmin}
                                  name="mbpRate"
                                  placeholder="0.00"
                                  className="full-width"
                                  precision={2}
                                  onValueChange={() => calculateMBPRateValue()}
                                />
                              </FormItem>
                            </Col>
                            <Col xs={24} lg={12}>
                              <FormItem label="MBP Portion" name="mbpPortion">
                                <InputNumber
                                  disabled
                                  value={getSafeComputedValue(
                                    FinancialInfoCalculator.getMbpPortionValue(
                                      values,
                                    ),
                                  )}
                                  allowNegative
                                  name="mbpPortion"
                                  inputPrefix={'$'}
                                  placeholder="0.00"
                                  className="full-width"
                                  precision={2}
                                />
                              </FormItem>
                            </Col>
                          </>
                        )}

                        <Col xs={24} lg={12}>
                          {checkIsCountriesWithStatesDropdown(
                            workSiteWorkLocation.country,
                          ) ? (
                            <FormItem label="Work State" name="workState">
                              <FormikSelectWithLoading
                                isLoading={isStatesLoading}
                                disabled
                                name="workState"
                                showSearch
                                showArrow={false}
                                optionFilterProp="children"
                                value={workSiteWorkLocation.state || undefined}
                              >
                                {states.map((state) => (
                                  <FormikSelectWithLoading.Option
                                    key={`select-work-state-${state.id}`}
                                    value={state.id || -1}
                                  >
                                    {state.name}
                                  </FormikSelectWithLoading.Option>
                                ))}
                              </FormikSelectWithLoading>
                            </FormItem>
                          ) : (
                            <FormItem
                              label="Work State"
                              name="workInternationalState"
                            >
                              <Input
                                name="workInternationalState"
                                disabled
                                value={
                                  workSiteWorkLocation.internationalStateName ||
                                  undefined
                                }
                              />
                            </FormItem>
                          )}
                        </Col>
                      </Row>
                    </Col>

                    <Divider className="modal-divider after-form hidden-md-up" />

                    <Col xs={24} md={12}>
                      <div className="modal-title">Markup Calculator</div>

                      <Row gutter={20}>
                        {!isTeammate && (
                          <Col xs={24} lg={12}>
                            <FormItem name="marginPercent" label="Margin %">
                              <InputNumber
                                disabled
                                value={getSafeComputedValue(
                                  FinancialInfoCalculator.getMarginPercentValue(
                                    values,
                                    mspOrVmsOverviewInfo,
                                  ),
                                )}
                                allowNegative
                                name="marginPercent"
                                placeholder="0.00"
                                className="full-width"
                                precision={2}
                              />
                            </FormItem>
                          </Col>
                        )}
                        <Col xs={24} lg={12}>
                          <FormItem label="Markup %" name="markupPercent">
                            <InputNumber
                              disabled
                              value={getSafeComputedValue(
                                FinancialInfoCalculator.getMarkupPercentValue(
                                  values,
                                ),
                              )}
                              allowNegative
                              name="markupPercent"
                              placeholder="0.00"
                              className="full-width"
                              precision={2}
                            />
                          </FormItem>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {financialWarningMessageExists && (
                    <ul
                      className={cx('hidden-md-down icon-list', styles.hintMd)}
                    >
                      {financialWarningMessages.map((message, index) => (
                        <li key={`financial-message-${index}`}>
                          <span className="icon icon-error" />
                          <span>{message}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </FinancialRatesFieldArray>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
});
